<script setup lang="ts">
const { t, locale } = useI18n()

// Create a ref for the placeholder element
const cookiebotContainer = ref(null)
onMounted(() => {
    if (cookiebotContainer.value) {
        // Create script element
        const script = document.createElement('script')
        script.id = 'CookieDeclaration'
        script.src = 'https://consent.cookiebot.com/e00d870c-bbc2-486f-b094-db01392180f4/cd.js'
        script.type = 'text/javascript'
        script.async = true
        script.setAttribute('data-culture', locale.value)

        // Append the script to the placeholder element
        cookiebotContainer.value.appendChild(script)

        // Optional: Add event listener for Cookiebot events
        script.onload = () => {
            console.log('Cookiebot script loaded.')
            // Add your code here to handle Cookiebot events or other actions
        }
    }
})
</script>

<template>
    <section class="dataprotection-wrapper">
        <MainHeadline :main-headline-config="{ title: t('dataProtection.headline') }" />

        <ol class="dataprotection-container">
            <li>
                <strong>Allgemeine Hinweise und Grundsätze der Datenverarbeitung</strong>
                <p>
                    Der Schutz der Privatsphäre und der persönlichen Daten von Ihnen und Ihrem Kind, der sog. personenbezogenen Daten, ist uns ein wichtiges Anliegen bei der Nutzung unserer Webseite.
                </p>
                <p>
                    Personenbezogene Daten sind nach Art. 4 Nr. 1 DS-GVO alle Informationen, die sich auf eine identifizierte oder identifizierbare natürliche Person beziehen. Hierzu gehören beispielsweise Informationen wie Ihr Vor- und Nachname, Ihre Anschrift, Ihre Telefonnummer, Ihre E-Mail-Adresse und Ihre IP-Adresse.
                </p>
                <p>
                    Daten, bei denen kein Bezug zu Ihrer Person herstellbar ist, wie beispielsweise anonymisierte Angaben, sind keine personenbezogenen Daten. Die Verarbeitung (z.B. das Erheben, das Speichern, das Auslesen, das Abfragen, die Verwendung, die Übermittlung, das Löschen oder die Vernichtung) nach Art. 4 Nr. 2 DS-GVO bedarf immer Ihrer Einwilligung oder einer anderweitigen gesetzlichen Rechtsgrundlage. Verarbeitete personenbezogene Daten müssen gelöscht werden, sobald der Zweck der Verarbeitung erreicht wurde und keine gesetzlich vorgeschriebenen Aufbewahrungspflichten mehr zu wahren sind.
                </p>
                <p>
                    Hier finden Sie Informationen über den Umgang mit Ihren personenbezogenen Daten beim Besuch unserer Webseite. Zur Bereitstellung der Funktionen und Dienste unserer Webseite ist es erforderlich, dass wir personenbezogene Daten über Sie und Ihr Kind erheben.
                </p>
                <p>
                    Nachfolgend finden Sie auch Informationen zu Art und Umfang der jeweiligen Datenverarbeitung, den Zweck und die entsprechende Rechtsgrundlage sowie die jeweilige Speicherdauer.
                </p>
                <p>
                    Diese Datenschutzerklärung gilt nur für diese Webseite. Sie gilt nicht für Webseiten von Dritten, auf die wir durch einen Hyperlink lediglich verweisen. Wir können keine Verantwortung für den vertraulichen Umgang Ihrer personenbezogenen Daten auf diesen Webseiten Dritter übernehmen, da wir keinen Einfluss darauf haben, ob diese die gesetzlichen Datenschutzbestimmungen einhalten. Über den Umgang mit Ihren personenbezogenen Daten durch Dritte informieren Sie sich bitte direkt auf deren Webseiten.
                </p>
            </li>
            <li>
                <strong>Verantwortliche Stelle</strong>

                <p>
                    Verantwortlich für die Verarbeitung von personenbezogenen Daten auf dieser Webseite ist (vgl. Impressum):
                </p>

                <address v-html="t('footer.address')" />
                <p v-html="t('footer.contact')" />
            </li>
            <li>
                <strong>Datenschutzbeauftragter</strong>
                <p>
                    Sie können sich bei Fragen zum Datenschutz auch jederzeit an unseren Datenschutzbeauftragten wenden:

                    [Kontaktdaten]
                </p>
            </li>
            <li>
                <strong>Bereitstellung und Nutzung der Webseite / Server Logfiles</strong>

                <ol>
                    <li>
                        <strong>Art und Umfang der Datenverarbeitung</strong>

                        <p>Wenn Sie diese Webseite nutzen, ohne anderweitig (z.B. durch Registrierung oder Nutzung des Kontaktformulars) Daten an uns zu übermitteln, erheben wir technisch notwendige Daten in Form von Protokolldaten (sog. Logfiles), welche von Ihrem Endgerät automatisch an unseren Server übermittelt werden, u.a.:</p>

                        <ul>
                            <li>IP-Adresse</li>
                            <li>Datum und Uhrzeit der Anfrage</li>
                            <li>URL der abgerufenen Unterseite</li>
                            <li>URL der Seite, von der aus die Weiterleitung auf unsere Seite erfolgt ist (sog. Referrer-URL)</li>
                            <li>Zugriffsstatus/HTTP-Statuscode</li>
                            <li>Typ, Sprache und Version der Browsersoftware</li>
                            <li>Betriebssystem</li>
                        </ul>
                    </li>
                    <li>
                        <strong>Zweck und Rechtsgrundlage der Datenverarbeitung</strong>
                        <p>
                            Diese Verarbeitung ist technisch erforderlich, um Ihnen unsere Webseite anzeigen zu können. Wir nutzen die Daten auch, um die Sicherheit und Stabilität unserer Webseite zu gewährleisten.
                        </p>

                        <p>
                            Rechtsgrundlage für die Verarbeitung ist Art. 6 Abs. 1 lit. f) DS-GVO. Die Verarbeitung der genannten Daten ist für die Bereitstellung einer Webseite erforderlich und dient damit der Wahrung eines berechtigten Interesses unseres Unternehmens.
                        </p>
                    </li>
                    <li>
                        <strong>Speicherdauer</strong>
                        <p>
                            Sobald die genannten personenbezogenen Daten zur Anzeige der Webseite nicht mehr erforderlich sind, werden diese gelöscht. Dies ist spätestens sieben Tage nach Besuch unserer Webseite der Fall. Die Erfassung der Daten zur Bereitstellung der Webseite und die Speicherung der Daten in Logfiles ist für den Betrieb der Webseite zwingend erforderlich. Es besteht folglich bezüglich dieses Aspektes seitens des Nutzers keine Widerspruchsmöglichkeit. Eine weitergehende Speicherung kann im Einzelfall dann erfolgen, wenn dies gesetzlich vorgeschrieben ist.
                        </p>
                    </li>
                </ol>
            </li>
            <li>
                <strong>Formular zur Vermisstenmeldung</strong>
                <ol>
                    <li>
                        <strong>Art und Umfang der Datenverarbeitung</strong>
                        <p>
                            Auf unserer Webseite können Sie über ein bereitgestelltes Formular mit uns in Kontakt treten. Darin können Sie uns Angaben zu Ihrem Kind zukommen lassen, damit wir diese im Rahmen einer Suchaktion veröffentlichen, insb. in Postings auf unseren Präsenzen auf Facebook, Instagram und TikTok sowie auf Digitalflächen im Außenbereich (z.B. Werbeflächen) in der Stadt oder auf Fahrzeugen. Ihre Kontaktdaten verwenden wir zum Zwecke der Abstimmung der Suchaktion.
                        </p>
                        <p>
                            Wenn Sie von dem Formular Gebrauch machen, können darüber die folgenden personenbezogenen Daten verarbeitet werden:
                        </p>

                        <p><em>Zu Ihrem Kind:</em></p>

                        <ul>
                            <li>Vorname</li>
                            <li>Nachname</li>
                            <li>Rufname</li>
                            <li>Geschlecht</li>
                            <li>Geburtsdatum</li>
                            <li>Staatsangehörigkeit</li>
                            <li>Datum, Uhrzeit und Umstände des Verschwindens</li>
                            <li>Letzter bekannter Aufenthaltsort</li>
                            <li>Adressdaten und Koordinaten des Vermisstengebiets (wie über die Karte eingegeben)</li>
                            <li>Gesundheitliche Probleme oder besondere Bedürfnisse</li>
                            <li>Zusätzliche Informationen</li>
                            <li>Beschreibung und Foto</li>
                        </ul>

                        <p><em>Zu Ihnen:</em></p>

                        <ul>
                            <li>Vorname</li>
                            <li>Nachname</li>
                            <li>E-Mail-Adresse</li>
                            <li>Telefonnummer</li>
                        </ul>
                    </li>
                    <li>
                        <strong>Zweck und Rechtsgrundlage</strong>
                        <p>
                            Der Zweck der Datenverarbeitung ist die Abstimmung und Umsetzung der Suchaktion. Die Rechtsgrundlage für die Verarbeitung ist Ihre Einwilligung nach Art. 6 Abs. 1 lit. a) DS-GVO bzw. Art. 9 Abs. 2 lit. a) DS-GVO, welche Sie jederzeit für die Zukunft widerrufen können.
                        </p>
                        <p>
                            Im Falle des Widerrufs Ihrer Einwilligung entfernen oder anonymisieren wir die veröffentlichten Daten (z.B. Verpixeln von Fotos).
                        </p>
                    </li>

                    <li>
                        <strong>Speicherdauer</strong>
                        <p>
                            Die von Ihnen in das Formular eingegebenen Daten werden veröffentlicht, bis Sie uns zur Löschung auffordern oder die Suchaktion beendet ist.
                        </p>
                    </li>
                </ol>
            </li>
            <li>
                <strong>Kontaktmöglichkeiten per E-Mail</strong>
                <p>
                    Sie können sich an uns per E-Mail wenden. Unsere Datenerhebung beschränkt sich dabei auf die E-Mail-Adresse des von Ihnen zur Kontaktaufnahme verwendeten E-Mail-Accounts, die Metadaten (Zeitstempel, weitere Empfänger) sowie auf die von Ihnen im Rahmen der Kontaktaufnahme beliebig zur Verfügung gestellten personenbezogenen Daten.
                </p>
                <p>
                    Bitte beachten Sie, dass E-Mails grundsätzlich unverschlüsselt versendet werden und damit eine Kenntnisnahme durch Dritte nicht ausgeschlossen werden kann. Für eine verschlüsselte Datenübertragung verwenden Sie bitte das Formular auf dieser Webseite.
                </p>
            </li>

            <li>
                <strong>Cookiebot</strong>

                <p>
                    Wir nutzen das Consent Management-Tool „Cookiebot“ zur Erfüllung gesetzlicher Verpflichtungen nach Art. 6 Abs. 1 lit. c DSGVO, insbesondere der Verwaltung von Cookie-Einwilligungen. Betreiber ist die Usercentrics A/S, Havnegade 39, 1058 Kopenhagen, Dänemark.
                </p>
                <p>
                    Cookiebot sammelt Logfile-Daten und Einwilligungsdaten mittels JavaScript. Dieses JavaScript ermöglicht es Cookiebot, den Nutzer über seine Einwilligung zu bestimmten Tags auf unserer Website zu informieren und diese einzuholen, zu verwalten und zu dokumentieren.
                    Dabei handelt es sich um folgende Datenkategorien:
                </p>

                <ul>
                    <li>Anonymisierte IP-Adresse</li>
                    <li>Datum und Uhrzeit der Einwilligung</li>
                    <li>Browser-Informationen</li>
                    <li>Die URL unserer Unterseite, von welcher aus Sie Ihre Einwilligung erteilt haben</li>
                    <li>Ein zufälliger und verschlüsselter Key</li>
                    <li>Der Einwilligungsstatus, welcher als Nachweis der Einwilligung dient</li>
                </ul>
                <p>
                    Die mithilfe von Cookiebot erteilten Cookie-Einwilligungen werden für einen Zeitraum von 12 Monaten gespeichert.
                </p>
                <p>
                    Die Rechtsgrundlage der Datenverarbeitung ist die Erfüllung gesetzlicher Verpflichtungen Art. 6 Abs. 1 lit. c DSGVO.
                </p>
                <p>
                    Weitere Informationen finden Sie in der Datenschutzerklärung von Cookiebot unter: https://www.cookiebot.com/de/privacy-policy/
                </p><p />
            </li>
            <li>
                <strong>Google Analytics</strong>

                <p>
                    Wir setzen auf unser Webseite das Tracking-Werkzeug Google Analytics der Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland, Tel: +353 1 543 1000, Fax: +353 1 686 5660, E-Mail: support-deutschland@google.com ("Google") ein.
                </p>
                <p>
                    Google Analytics verwendet sog. "Cookies", Textdateien, die auf Ihrem Computer gespeichert werden und die eine Analyse der Benutzung der Webseite ermöglichen.
                </p>
                <p>
                    Die durch den Cookie erzeugten Informationen über Ihre Benutzung dieser Webseite werden in der Regel an einen Server von Google in den USA übertragen und dort gespeichert.
                </p>
                <p>
                    Im Auftrag des Betreibers dieser Webseite wird Google diese Informationen benutzen, um Ihre Nutzung der Webseite systematisch auszuwerten, um Reports über die Webseitenaktivitäten zusammenzustellen und um weitere mit der Webseitennutzung und der Internetnutzung verbundene Dienstleistungen gegenüber dem Webseitenbetreiber zu erbringen.
                </p>
                <p>
                    Werden Einzelseiten unserer Webseite aufgerufen, so werden folgende Daten gespeichert:
                </p>
                <ul>
                    <li>Drei Bytes der IP-Adresse des aufrufenden Systems des Nutzers (anonymisierte IP-Adresse)</li>
                    <li>Die aufgerufene Webseite</li>
                    <li>Die Webseite, von der der Nutzer auf die aufgerufene Seite unserer Webseite gelangt ist (Referral-URL)</li>
                    <li>Die Unterseiten, die von der aufgerufenen Seite aufgerufen werden</li>
                    <li>Die Verweildauer auf der Webseite</li>
                    <li>Die Häufigkeit des Aufrufs der Webseite</li>
                </ul>

                <p>
                    Wir nutzen Google Analytics mit aktivierter IP-Anonymisierung. Damit werden die
                    IP-Adressen um das letzte Oktett gekürzt (Bsp: 192.168.79.***; sog. IP-Masking). Eine Zuordnung der gekürzten IP-Adresse zum aufrufenden Rechner bzw. Endgeräts des Nutzers ist nicht mehr möglich.
                </p>

                <p>
                    Der Dienst Google Analytics dient zur Analyse des Nutzungsverhaltens unseres Internetauftritts. Rechtsgrundlage ist Ihre Einwilligung nach Art. 6 Abs. 1 lit. a) DS-GVO.
                </p>
                <p>
                    Die gespeicherten Daten werden von uns gelöscht, sobald sie für unsere Aufzeichnungszwecke nicht mehr benötigt werden. In unserem Fall beträgt die Speicherdauer maximal 24 Monate.
                </p>

                <p>
                    Nähere Informationen zu den Nutzungsbedingungen von Google Analytics:
                    www.google.com/analytics/terms/de.html
                </p>

                <p>
                    Nähere Informationen zum Datenschutz von Google Analytics:
                    <NuxtLink to=" https://support.google.com/analytics/answer/6004245?hl=de" target="_blank" rel="noopener noreferrer">
                        https://support.google.com/analytics/answer/6004245?hl=de
                    </NuxtLink>
                </p>
            </li>
            <li>
                <strong>Google Maps</strong>
                <p>
                    Wir binden die Landkarten des Dienstes Google Maps ein. Google Maps ist ein Diest von Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland, Tel: +353 1 543 1000, Fax: +353 1 686 5660, E-Mail: support-deutschland@google.com ("Google").
                </p>

                <p>
                    Dadurch können wir Ihnen interaktive Karten direkt auf unserer Webseite anzeigen und ermöglichen Ihnen die Nutzung der Karten-Funktion. Auf der Karte können Sie den Bereich des letzten bekannten Aufenthalts Ihres Kindes markieren.
                </p>
                <p>
                    Sofern Sie die auf unserer Webseite eingebundene Komponente Google Maps aufrufen, speichert Google über Ihren Internet-Browser ein Cookie auf Ihrem Endgerät und verarbeitet folgende Daten:
                </p>
                <ul>
                    <li>Das Betriebssystem</li>
                    <li>Informationen über den Browsertyp und die verwendete Version</li>
                    <li>Informationen zu Ihrem Internet-Service-Provider</li>
                    <li>Ihre IP-Adresse</li>
                    <li>Datum und Uhrzeit des Zugriffs</li>
                    <li>Webseiten, von denen aus Sie auf unsere Internetseite gelangt sind</li>
                    <li>Webseiten, die Sie über unsere Website aufrufen</li>
                </ul>
                <p>
                    Dies erfolgt unabhängig davon, ob Google ein Nutzerkonto bereitstellt, über das Sie eingeloggt sind, oder ob kein Nutzerkonto besteht. Wenn Sie bei Google eingeloggt sind, können Ihre Daten Ihrem Konto zugeordnet werden. Wenn Sie die Zuordnung mit Ihrem Profil bei Google nicht wünschen, müssen Sie sich ausloggen.
                </p>
                <p>
                    Google speichert Nutzerdaten als Nutzungsprofile und nutzt sie auch für Zwecke der Werbung und Marktforschung. Eine solche Auswertung erfolgt insbesondere (auch für nicht eingeloggte Nutzer) zur Erbringung von personalisierter Werbung.
                </p>

                <p>  Rechtsgrundlage der Verarbeitung ist Ihre Einwilligung nach Art. 6 Abs. 1 lit. a) DS-GVO.</p>
            </li>

            <li>
                <strong>Datenübermittlung</strong>
                <p>      Wir geben Ihre personenbezogenen Daten nur an Dritte weiter, wenn:</p>

                <ul>
                    <li>Sie hierfür im Einzelfall Ihre ausdrückliche Einwilligung nach Art. 6 Abs. 1 lit. a) DS-GVO erteilt haben;</li>
                    <li>dies gesetzlich zulässig und nach Art. 6 Abs. 1 lit. b) DS-GVO zur Erfüllung eines Vertragsverhältnisses mit Ihnen oder der Durchführung vorvertraglicher Maßnahmen erforderlich ist (z.B. an Zahlungs-, Versand-, Liefer- oder Inkassodienstleister);</li>
                    <li>nach Art. 6 Abs. 1 lit. c) DS-GVO für die Weitergabe eine rechtliche Verpflichtung besteht (z.B. an Behörden, Sozialversicherungsträger, Krankenkassen, Aufsichtsbehörden und Strafverfolgungsbehörden);</li>
                    <li>die Weitergabe nach Art. 6 Abs. 1 lit. f) DS-GVO zur Wahrung berechtigter Vereinsinteressen, sowie zur Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen erforderlich ist und kein Grund zur Annahme besteht, dass Sie ein überwiegendes schutzwürdiges Interesse an der Nichtweitergabe Ihrer Daten haben (z.B. an Inkassodienstleister);</li>
                    <li>wir uns bei der Verarbeitung nach Art. 28 DS-GVO externer Dienstleister (sog. Auftragsverarbeiter) bedienen, welche nach unseren Weisungen Daten verarbeiten und zum sorgfältigen Umgang mit Ihren Daten verpflichtet sind (z.B. IT-Dienstleister).</li>
                </ul>

                <p>
                    Soweit wir die Daten Ihrer Vermisstenmeldung auf unserer Facebook-Seite veröffentlichen, ist die Facebook-Muttergesellschaft Meta Platforms, Inc. in den USA nach dem sog. EU-US-Data Privacy Framework zertifiziert. Dies bedeutet, dass die EU-Kommission festgestellt hat, dass Meta Platforms einen Datenschutzstandard einhält, welcher dem europäischen im Wesentlichen entspricht (Art. 45 DS-GVO).
                </p>
            </li>
            <li>
                <strong>Datensicherheit und Sicherungsmaßnahmen</strong>

                <p>
                    Wir verpflichten uns, Ihre Privatsphäre zu schützen und Ihre personenbezogenen Daten vertraulich zu behandeln. Dazu treffen wir umfangreiche technische und organisatorische Sicherheitsvorkehrungen, welche regelmäßig überprüft und dem technologischen Fortschritt angepasst werden. Hierzu gehört unter anderem die Verwendung anerkannter Verschlüsselungsverfahren (SSL oder TLS).

                    Unverschlüsselt preisgegebene Daten, beispielsweise wenn dies per unverschlüsselter E-Mail erfolgt, können jedoch eventuell von Dritten mitgelesen werden. Darauf haben wir keinen Einfluss. Es liegt dann im Verantwortungsbereich des Nutzers, die zur Verfügung gestellten Daten durch Verschlüsselung oder in sonstiger Weise gegen Missbrauch zu schützen.
                </p>
            </li>
            <li>
                <strong>Änderungen der Datenschutzerklärung</strong>

                <p>
                    Wir behalten uns das Recht vor, diese Datenschutzerklärung bei Bedarf jederzeit entsprechend zu aktualisieren.
                </p>
            </li>
            <li>
                <strong>Ihre gesetzlichen Rechte</strong>

                <p>
                    Nachfolgend finden Sie Ihre gesetzlichen Rechte in Bezug auf Ihre personenbezogenen Daten. Einzelheiten dazu ergeben sich aus den Artikeln 7, 15-22 und 77 DS-GVO. Sie können sich diesbezüglich an uns als die verantwortliche Stelle oder an unseren Datenschutzbeauftragten wenden.
                </p>

                <ol>
                    <li>
                        Recht auf Widerruf Ihrer datenschutzrechtlichen Einwilligung nach Art. 7 Abs. 3 S. 1 DS-GVO
                        <p>
                            Sie können eine Einwilligung in die Verarbeitung Ihrer personenbezogenen Daten jederzeit mit Wirkung für die Zukunft widerrufen. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Verarbeitung wird davon jedoch nicht berührt.
                        </p>
                    </li>
                    <li>
                        <strong>Auskunftsrecht nach Art. 15 DS-GVO</strong>
                        <p>
                            Sie haben das Recht, eine Bestätigung darüber zu verlangen, ob wir personenbezogene Daten verarbeiten, die Sie betreffen. Ist dies der Fall, so haben Sie das Recht auf Auskunft über diese personenbezogenen Daten sowie auf weitere Informationen, z.B. die Verarbeitungszwecke, die Kategorien verarbeiteter personenbezogener Daten, die Empfänger und die geplante Dauer der Speicherung bzw. die Kriterien für die Festlegung der Dauer.
                        </p>
                    </li>

                    <li>
                        <strong>Recht auf Berichtigung und Vervollständigung nach Art. 16 DS-GVO</strong>
                        <p>
                            Sie haben das Recht, unverzüglich die Berichtigung unrichtiger Daten zu verlangen. Unter Berücksichtigung der Zwecke der Verarbeitung haben Sie das Recht, die Vervollständigung unvollständiger Daten zu verlangen.
                        </p>
                    </li>

                    <li>
                        <strong>Recht auf Löschung („Recht auf Vergessenwerden“) nach Art. 17 DS-GVO</strong>
                        <p>
                            Sie haben ein Recht zur Löschung, soweit die Verarbeitung nicht erforderlich ist. Dies ist beispielsweise dann der Fall, wenn Ihre Daten für die ursprünglichen Zwecke nicht mehr erforderlich sind, Sie Ihre datenschutzrechtliche Einwilligungserklärung widerrufen haben oder die Daten unrechtmäßig verarbeitet wurden.
                        </p>
                    </li>

                    <li>
                        <strong>Recht auf Einschränkung der Verarbeitung nach Art. 18 DS-GVO</strong>
                        <p>
                            Sie haben ein Recht auf Einschränkung der Verarbeitung, z.B. wenn Sie der Meinung sind, die personenbezogenen Daten seien unrichtig.
                        </p>
                    </li>

                    <li>
                        <strong>Recht auf Datenübertragbarkeit nach Art. 20 DS-GVO</strong>
                        <p>
                            Sie haben das Recht, die Sie betreffenden personenbezogenen Daten, welche Sie uns bereitgestellt haben, in einem strukturierten, gängigen und maschinenlesbaren Format zu erhalten.
                        </p>
                    </li>

                    <li>
                        <strong>Widerspruchsrecht nach Art. 21 DS-GVO</strong>
                        <p>
                            Sie haben das Recht, jederzeit aus Gründen, die sich aus Ihrer besonderen Situation ergeben, gegen die Verarbeitung bestimmter Sie betreffender personenbezogener Daten Widerspruch einzulegen.
                            Im Falle von Direktwerbung haben Sie als betroffene Person das Recht, jederzeit Widerspruch gegen die Verarbeitung Sie betreffender personenbezogener Daten zum Zwecke derartiger Werbung einzulegen; dies gilt auch für Profiling, soweit es mit solcher Direktwerbung in Verbindung steht.
                        </p>
                    </li>

                    <li>
                        <strong>Automatisierte Entscheidung im Einzelfall einschließlich Profiling nach Art. 22 DS-GVO</strong>
                        <p>
                            Sie haben das Recht, nicht einer ausschließlich auf einer automatisierten Verarbeitung – einschließlich Profiling – beruhenden Entscheidung unterworfen zu werden, außer in den in Art. 22 DS-GVO erwähnten Ausnahmetatbeständen.
                            Eine ausschließlich auf einer automatisierten Verarbeitung – einschließlich Profiling – beruhende Entscheidungsfindung findet nicht statt.
                        </p>
                    </li>
                    <li>
                        <strong>Beschwerde bei einer Datenschutzaufsichtsbehörde nach Art. 77 DS-GVO</strong>
                        <p>
                            Außerdem können Sie jederzeit Beschwerde bei einer Datenschutzaufsichtsbehörde einlegen, beispielsweise wenn Sie der Meinung sind, dass die Datenverarbeitung nicht im Einklang mit datenschutzrechtlichen Vorschriften steht.
                        </p>
                    </li>
                </ol>
            </li>
        </ol>
        <div ref="cookiebotContainer" class="cookiebot-container" />
    </section>
</template>

<style  lang="scss">
.dataprotection-wrapper {
    grid-column: 2 / -2;
    text-align: center;
    @include section-padding($first: true);
}

.dataprotection-container {
    text-align: left;
    list-style: none;
    padding-left: 0;
    margin: 0;

    > li {
        counter-increment: list-item;
        margin-block-end: px2rem(40);
        position: relative;

        &::before {
            content: counter(list-item) ". ";
            font-weight: 800;
            font-family: $font-headline;
            font-size: clamp(18px, 4vw, 30px);
        }

        > strong {
            @include form-headline;
        }

        > ol {
            list-style: none;
            padding: 10px 0 0;
            counter-reset: custom-counter;

            > li {
                counter-increment: custom-counter;
                position: relative;
                margin-block-end: px2rem(30);

                &::before {
                    content: counter(custom-counter, lower-alpha) ") ";
                    font-weight: 800;
                    font-family: $font-headline;
                    padding-inline-end: 3px;
                }
            }
        }

        address {
            font-style: normal;
        }

        a {
            color: inherit;
            text-decoration: underline;
        }

        ul {
            margin: 0 0 px2rem(40) px2rem(25);
            padding: 0;
            list-style-type: disc;
        }
    }
}

.cookiebot-container {
    text-align: left;

    a {
        text-decoration: underline;
        color: currentcolor;
    }

    /* stylelint-disable selector-class-pattern */
    .CookieDeclarationType {
        border: $white 1px solid;
    }

    table {
        border: $white 1px solid;

        th,
        tr,
        td {
            border-bottom: $white 1px solid;
        }
    }
}
</style>
